import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';

import { radius } from 'styles/revampTokens';

interface Properties {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, description: string) => void;
  title?: string;
  submitLabel?: string;
  initialName?: string;
  initialDescription?: string;
}

const MAX_DESCRIPTION_WORDS = 20;

const AddDimensionModal = ({
  isOpen,
  onClose,
  onCreate,
  title = 'Add Dimension',
  submitLabel = 'Create Dimension',
  initialName = '',
  initialDescription = '',
}: Properties) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const descriptionWordCount = useMemo(() => {
    const words = description.trim().split(/\s+/).filter(Boolean);
    return description.trim() ? words.length : 0;
  }, [description]);

  const canCreate = Boolean(name.trim()) && descriptionWordCount <= MAX_DESCRIPTION_WORDS;

  const handleCreate = () => {
    if (!canCreate) return;
    onCreate(name.trim(), description.trim());
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered blockScrollOnMount={false}>
      <ModalOverlay />
      <ModalContent width='412px' maxWidth='90vw' borderRadius={radius.overlay} p='16px'>
        <ModalBody p={0}>
          <Flex direction='column' align='center' gap='20px'>
            <Text
              textAlign='center'
              fontFamily='Comfortaa Bold'
              fontWeight='700'
              fontSize='18px'
              color='#121826'
            >
              {title}
            </Text>
            <Flex direction='column' gap='16px' width='100%'>
              <Flex direction='column' gap='4px' width='100%'>
                <Text fontFamily='Roboto Regular' fontWeight='600' fontSize='11px' color='#708090'>
                  Name
                </Text>
                <Box
                  as='input'
                  value={name}
                  onChange={(event: any) => setName(event.target.value)}
                  onKeyDown={(event: any) => {
                    if (event?.key === 'Enter') handleCreate();
                  }}
                  placeholder='Enter dimension name here'
                  autoFocus
                  width='100%'
                  bg='white'
                  border='1px solid #D1D6DD'
                  borderRadius={radius.control}
                  p='12px'
                  fontFamily='Roboto Medium, Roboto Regular'
                  fontSize='13px'
                  color='#121826'
                  outline='none'
                  _placeholder={{ color: '#BEBEBE' }}
                  _focus={{ borderColor: '#8B5CF6' }}
                />
              </Flex>
              <Flex direction='column' gap='4px' width='100%'>
                <Flex justifyContent='space-between' width='100%'>
                  <Text
                    fontFamily='Roboto Regular'
                    fontWeight='600'
                    fontSize='11px'
                    color='#708090'
                  >
                    Description
                  </Text>
                  <Text
                    fontFamily='Roboto Regular'
                    fontWeight='600'
                    fontSize='11px'
                    color='#708090'
                  >
                    {MAX_DESCRIPTION_WORDS} words max
                  </Text>
                </Flex>
                <Textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder='Write something about this Dimension...'
                  height='59px'
                  resize='none'
                  bg='white'
                  border='1px solid #D1D6DD'
                  borderRadius={radius.control}
                  p='12px'
                  fontFamily='Roboto Medium, Roboto Regular'
                  fontSize='13px'
                  color='#121826'
                  _placeholder={{ color: '#BEBEBE' }}
                  _focus={{ borderColor: '#8B5CF6', boxShadow: 'none' }}
                />
                {descriptionWordCount > MAX_DESCRIPTION_WORDS && (
                  <Text fontSize='11px' color='#E23F3F' fontWeight='500'>
                    Please keep the description within {MAX_DESCRIPTION_WORDS} words.
                  </Text>
                )}
              </Flex>
            </Flex>
            <Flex gap='16px' width='100%' height='43px'>
              <Button
                flex={1}
                bg='white'
                border='1px solid #D1D6DD'
                borderRadius={radius.control}
                fontFamily='Roboto Medium, Roboto Regular'
                fontWeight='500'
                fontSize='13px'
                color='#121826'
                height='100%'
                transition='transform 0.1s ease, background 0.15s ease'
                _hover={{ bg: 'gray.50' }}
                _active={{ transform: 'scale(0.97)' }}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                flex={1}
                bg='#8B5CF6'
                borderRadius={radius.control}
                fontFamily='Roboto Medium, Roboto Regular'
                fontWeight='500'
                fontSize='13px'
                color='white'
                height='100%'
                isDisabled={!canCreate}
                transition='transform 0.1s ease, background 0.15s ease'
                _hover={{ bg: '#7C4EE0' }}
                _active={{ transform: 'scale(0.97)' }}
                onClick={handleCreate}
              >
                {submitLabel}
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddDimensionModal;
